/**
 * Modal com lançamentos de uma categoria do DRE (competência).
 */

import React, { useEffect, useState } from 'react'
import { ArrowUpRight, Loader2, Receipt } from 'lucide-react'
import Modal from '../../../../components/ERP/Modal/Modal'
import { dreService } from '../../../../services/dreService'
import { formatDateOnly } from '../../../../utils/dateUtils'
import {
  getFinancialTransactionNavigation,
  openFinancialTransactionInNewWindow
} from '../../../../utils/dfcTransactionNavigation'
import './DRECategoryTransactionsModal.css'

function DRECategoryTransactionsModal({
  isOpen,
  onClose,
  category,
  sectionType,
  month,
  year
}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  useEffect(() => {
    if (!isOpen || !category) return undefined

    let cancelled = false

    const loadTransactions = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await dreService.getCategoryTransactions({
          month,
          year,
          categoryId: category.categoryId,
          sectionType
        })

        if (!cancelled) {
          if (response.success) {
            setData(response.data)
          } else {
            setError('Erro ao carregar lançamentos da categoria')
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Erro ao carregar lançamentos DRE:', err)
          setError('Erro ao carregar lançamentos. Tente novamente.')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadTransactions()

    return () => {
      cancelled = true
    }
  }, [isOpen, category, sectionType, month, year])

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'R$ 0,00'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const handleOpenTransaction = (transaction) => {
    const navigation = getFinancialTransactionNavigation(transaction)
    if (!navigation) return

    onClose()
    openFinancialTransactionInNewWindow(transaction)
  }

  const transactions = data?.transactions || []
  const categoryLabel = category?.categoryName || category?.label || 'Categoria'

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={categoryLabel}
      subtitle={`Competência · ${category?.transactionCount || 0} lançamento(s)`}
      icon={<Receipt size={22} />}
      size="large"
    >
      <div className="dre-category-modal">
        <div className="dre-category-modal-summary">
          <div>
            <span className="dre-category-modal-summary-label">Total da categoria</span>
            <strong>{formatCurrency(Math.abs(data?.totalAmount ?? category?.value ?? 0))}</strong>
          </div>
          <div>
            <span className="dre-category-modal-summary-label">Período</span>
            <strong>{String(month).padStart(2, '0')}/{year}</strong>
          </div>
        </div>

        {loading && (
          <div className="dre-category-modal-loading">
            <Loader2 size={24} className="dre-category-modal-spinner" />
            <span>Carregando lançamentos...</span>
          </div>
        )}

        {error && (
          <div className="dre-category-modal-error">
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && transactions.length === 0 && (
          <div className="dre-category-modal-empty">
            <p>Nenhum lançamento encontrado para esta categoria.</p>
          </div>
        )}

        {!loading && !error && transactions.length > 0 && (
          <div className="dre-category-modal-list">
            {transactions.map((transaction) => {
              const navigation = getFinancialTransactionNavigation(transaction)
              const rowKey = `${transaction.source || 'cash_flow'}-${transaction.id}`

              return (
                <button
                  key={rowKey}
                  type="button"
                  className="dre-category-modal-item"
                  onClick={() => handleOpenTransaction(transaction)}
                  disabled={!navigation}
                  title={navigation ? 'Abrir lançamento em nova aba' : 'Lançamento sem destino disponível'}
                >
                  <div className="dre-category-modal-item-main">
                    <span className="dre-category-modal-item-date">
                      {formatDateOnly(transaction.date)}
                    </span>
                    <span className="dre-category-modal-item-description">
                      {transaction.description || 'Sem descrição'}
                    </span>
                    <span className="dre-category-modal-item-account">
                      {transaction.accountName || transaction.accountCode || 'Conta não informada'}
                    </span>
                  </div>
                  <div className="dre-category-modal-item-side">
                    <span className="dre-category-modal-item-amount">
                      {formatCurrency(transaction.amount)}
                    </span>
                    {navigation && <ArrowUpRight size={16} />}
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default DRECategoryTransactionsModal
